import type { SeatData } from '@/components/grid';

/**
 * Default grid dimension for legacy callers (onboarding stage, legacy plinth,
 * etc.). Runtime boards now pass their own `gridSize` through every renderer
 * entry point — this constant is retained so `import { GRID_SIZE }` keeps
 * working for the handful of places that genuinely want the 10×10 default.
 */
export const GRID_SIZE = 10;
export const TOTAL_TILES = GRID_SIZE * GRID_SIZE;
export const TILE_SPACING = 1.12;

/** Visual ordering of seats on the board: cheapest→priciest floor, or soonest→latest runway. */
export type BoardSortMode = 'floor' | 'time' | 'seat';

export interface TilePlacement {
  index: number;
  x: number;
  z: number;
  /** Retained as a field (all overlays consume it) but kept at a
   *  fixed value so tiles line up into a perfectly flat grid. Previously
   *  a pseudo-random "organic" jitter caused neighbouring tiles to read
   *  as misaligned from oblique camera angles — see user screenshot. */
  yJitter: number;
  rotJitter: number;
  scaleJitter: number;
}

/**
 * Returns an array of TilePlacements indexed by **seatIndex**.
 *
 * When `seats` is provided, the grid is ordered by `selfAssessedPrice`
 * **ascending** with the cheapest tile at the visual **top-left** of the
 * board (furthest row from camera, leftmost column) and the most expensive
 * at the **bottom-right** (nearest row, rightmost column). Within a row
 * tiles are laid out left→right by price; rows progress top→bottom.
 * Tie-broken by `seatIndex` so the order stays stable frame-to-frame.
 *
 * Camera sits at +Z looking at the origin, so a row at smaller Z reads as
 * "higher up" on screen and a row at larger Z reads as "closer / lower".
 * Mapping rank 0 to row 0 (and z = -offset) puts the cheapest tile in the
 * top-left of the visible board, matching the design spec.
 *
 * When `seats` is omitted, falls back to a deterministic row-by-row layout
 * that mirrors the seat index (legacy behaviour for callers that don't care
 * about price ordering).
 *
 * `gridSize` is optional and defaults to:
 *   • `seats.length`'s square root rounded up, if `seats` is provided and
 *     non-empty (so a 256-seat board auto-selects 16×16);
 *   • `GRID_SIZE` (10) otherwise — the legacy default.
 *
 * Callers that know their grid size (the lobby canvases) should pass it
 * explicitly so the "seat count doesn't match a perfect square" case still
 * lays out on the intended board dimensions.
 */
/**
 * Sort key buckets for `time` mode (lower = sorts first):
 *
 *  1. Priority-window forfeits (`antiSnipeEndsAt > now`) — these tiles are
 *     in their 5-min anti-snipe priority queue, the most urgent state on
 *     the board. We use a large negative offset so they sort strictly
 *     ahead of every active runway, sub-sorted by bucketed remaining
 *     priority time (same bucketing as runway tiles — stable across ticks).
 *     The offset (`-1e10`) is well below
 *     any realistic `runwaySeconds` value (~10^7 max for a fully-funded
 *     seat) so the bucket never collides with the active band.
 *
 *  2. Pending forfeits (`isPendingForfeit`) — deposit zeroed but no
 *     FORFEITED event yet (next poke will trip the priority window).
 *     These slot just behind priority-window tiles since action is
 *     equally imminent but the onchain transition hasn't happened yet.
 *
 *  3. Active held seats with positive runway — sort by bucketed runway
 *     (`RUNWAY_SORT_BUCKET_SEC`) ascending so tiles with similar time
 *     left don't bounce when seconds tick; tie-break is `seatIndex`.
 *
 *  4. Truly vacant / no holder / no priority context — sort to the very
 *     end with `MAX_SAFE_INTEGER`. These tiles have no "time" semantic.
 */

/** Bucket width (seconds) for `time` sort keys — avoids order churn when
 *  raw runway counts cross each other every tick while staying in the
 *  same coarse urgency band. */
export const RUNWAY_SORT_BUCKET_SEC = 45;

/**
 * Tolerance (seconds) for the anchor+predict stabilizer in
 * {@link stabilizeTimeKeys}. A held seat's runway decreases at exactly one
 * second per real second between genuine onchain events, so once anchored we
 * can *predict* its current runway precisely. The decayed `runwaySeconds`
 * feeding the sort, however, wobbles non-monotonically between SSE pushes and
 * the REST poll (the two sources sample the RPC balance at slightly different
 * moments, and the cached multicall can be ~25s stale). We keep a seat's
 * anchor — and therefore its rank — as long as the live sample stays within
 * this tolerance of the prediction; only a genuine jump (top-up, price change,
 * new holder) re-anchors it. Sized comfortably above the observed sample
 * wobble so noise of any bounded magnitude never re-ranks a tile, while real
 * changes (which dwarf it) always do. */
export const RUNWAY_ANCHOR_TOLERANCE_SEC = 45;

function bucketSecondsForSort(sec: number): number {
  return Math.floor(sec / RUNWAY_SORT_BUCKET_SEC) * RUNWAY_SORT_BUCKET_SEC;
}

/** Distinct key "regimes" a seat can occupy (see the sort-key bucket
 *  doc-comment above). Only the `active` band carries the wobbly decayed
 *  runway; the others are discrete states that re-rank the instant they
 *  change. */
type TimeRegime = 'antisnipe' | 'pending' | 'vacant' | 'active';

interface TimeClassification {
  regime: TimeRegime;
  /** The raw (bucketed) sort key — identical to the legacy `runwaySortKey`. */
  rawKey: number;
  /** Unbucketed runway seconds when `regime === 'active'`, else 0. */
  activeSeconds: number;
}

function classifyTime(s: SeatData, nowSec = Math.floor(Date.now() / 1000)): TimeClassification {
  if (s.antiSnipeEndsAt && s.antiSnipeEndsAt > nowSec) {
    const rem = s.antiSnipeEndsAt - nowSec;
    return { regime: 'antisnipe', rawKey: -1e10 + bucketSecondsForSort(rem), activeSeconds: 0 };
  }

  if (s.isPendingForfeit) return { regime: 'pending', rawKey: -1e9, activeSeconds: 0 };

  if (!s.holder || s.isForfeited) return { regime: 'vacant', rawKey: Number.MAX_SAFE_INTEGER, activeSeconds: 0 };
  const r = s.runwaySeconds;
  if (r <= 0) return { regime: 'vacant', rawKey: Number.MAX_SAFE_INTEGER, activeSeconds: 0 };
  return { regime: 'active', rawKey: bucketSecondsForSort(r), activeSeconds: r };
}

/** Raw (memory-free) `time` sort key for a seat — bucketed runway, used by the
 *  My Tiles panel and as the non-stabilized board fallback. */
export function timeSortKey(s: SeatData): number {
  return classifyTime(s).rawKey;
}

/** Per-seat anchor for the active-runway band: the runway we last trusted
 *  (`anchorSeconds`) and the wall-clock second we trusted it at
 *  (`anchorAtSec`). Predicted runway at time `now` is
 *  `anchorSeconds - (now - anchorAtSec)`. */
export interface RunwayAnchor {
  anchorSeconds: number;
  anchorAtSec: number;
}

export interface StabilizedTimeKeys {
  /** seatIndex → comparator key. Active seats use an order-preserving anchor
   *  key (see below); sentinel regimes use their raw key. */
  keys: Map<number, number>;
  /** seatIndex → anchor, for active seats only. Feed back in as `prevAnchors`
   *  on the next call. Entries for departed / non-active seats are dropped. */
  anchors: Map<number, RunwayAnchor>;
}

/**
 * Produce a churn-free committed sort-key map for the `time` board order via
 * anchor+predict.
 *
 * A held seat's runway decays at exactly 1s per real second between onchain
 * events, so once anchored we predict its current value and ignore sample
 * wobble within {@link RUNWAY_ANCHOR_TOLERANCE_SEC}; only a genuine jump
 * re-anchors it. The active sort key is `anchorSeconds + anchorAtSec`:
 * because every seat shares the same `now`, ordering by this sum is identical
 * to ordering by predicted runway, yet the value is **constant between
 * re-anchors** — so two seats decaying in lockstep never cross, and the key
 * map keeps a stable identity until something genuinely changes. No bucketing
 * is applied to the active band, so the old bucket-boundary swap is gone too.
 *
 * Pure: takes the previous anchors, returns fresh `keys` + `anchors` covering
 * only the seats passed in. `nowSec` is injected so callers (and tests) own
 * the clock.
 */
export function stabilizeTimeKeys(
  seats: SeatData[],
  prevAnchors: ReadonlyMap<number, RunwayAnchor>,
  nowSec: number = Math.floor(Date.now() / 1000),
): StabilizedTimeKeys {
  const keys = new Map<number, number>();
  const anchors = new Map<number, RunwayAnchor>();
  for (const seat of seats) {
    const c = classifyTime(seat, nowSec);
    if (c.regime !== 'active') {
      keys.set(seat.seatIndex, c.rawKey);
      continue; // sentinel regimes carry no anchor
    }
    const prev = prevAnchors.get(seat.seatIndex);
    let anchor: RunwayAnchor;
    if (prev) {
      const predicted = prev.anchorSeconds - (nowSec - prev.anchorAtSec);
      // Within tolerance → trust the prediction (reuse the same anchor object
      // so `anchorAtSec` — and the rank — stay put). Otherwise a genuine jump:
      // re-anchor to the fresh sample.
      anchor =
        Math.abs(c.activeSeconds - predicted) <= RUNWAY_ANCHOR_TOLERANCE_SEC
          ? prev
          : { anchorSeconds: c.activeSeconds, anchorAtSec: nowSec };
    } else {
      anchor = { anchorSeconds: c.activeSeconds, anchorAtSec: nowSec };
    }
    anchors.set(seat.seatIndex, anchor);
    keys.set(seat.seatIndex, anchor.anchorSeconds + anchor.anchorAtSec);
  }
  return { keys, anchors };
}

/**
 * Comparator matching `sortedSeatsForBoard(..., 'time')` — use anywhere a
 * runway ordering must agree with the board (e.g. My Tiles panel).
 *
 * `timeKeyOverride` lets callers supply a stabilized committed-key map
 * (see {@link stabilizeTimeKeys}) so the board order resists sample-noise
 * churn; seats absent from the map fall back to the live key.
 */
export function compareSeatsBoardTimeOrder(
  a: SeatData,
  b: SeatData,
  timeKeyOverride?: ReadonlyMap<number, number>,
): number {
  const ar = timeKeyOverride?.get(a.seatIndex) ?? timeSortKey(a);
  const br = timeKeyOverride?.get(b.seatIndex) ?? timeSortKey(b);
  if (ar !== br) return ar - br;
  return a.seatIndex - b.seatIndex;
}

/** Rewrites a sorted seat list so pinned indices occupy fixed ranks (for the
 *  duration of a buyout / claim cinematic before the board resort applies). */
function applySortLayoutPins(
  sorted: SeatData[],
  pins: ReadonlyMap<number, number> | undefined,
): SeatData[] {
  if (!pins || pins.size === 0) return sorted;
  let order = sorted.map((s) => s.seatIndex);
  const pinned = [...pins.entries()].sort((a, b) => b[1] - a[1]);
  for (const [seatIdx, targetRank] of pinned) {
    const i = order.indexOf(seatIdx);
    if (i === -1) continue;
    order.splice(i, 1);
    const insertAt = Math.min(Math.max(0, targetRank), order.length);
    order.splice(insertAt, 0, seatIdx);
  }
  const byIdx = new Map(sorted.map((s) => [s.seatIndex, s]));
  return order
    .map((idx) => byIdx.get(idx))
    .filter((s): s is SeatData => s !== undefined);
}

/** Stable sort for lobby 2D/3D — floor = price ascending, time = shortest runway first. */
export function sortedSeatsForBoard(
  seats: SeatData[],
  sortMode: BoardSortMode,
  sortLayoutPins?: ReadonlyMap<number, number>,
  timeKeyOverride?: ReadonlyMap<number, number>,
): SeatData[] {
  const sorted = [...seats].sort((a, b) => {
    // `seat` = fixed canonical layout (seat 0 top-left, row-major). Tiles
    // never reorder as prices change — used by the replay so the board is
    // a stable grid and takeovers land in place. No layout pins needed.
    if (sortMode === 'seat') {
      return a.seatIndex - b.seatIndex;
    }
    if (sortMode === 'time') {
      return compareSeatsBoardTimeOrder(a, b, timeKeyOverride);
    }
    const ap = a.selfAssessedPrice;
    const bp = b.selfAssessedPrice;
    if (ap === bp) return a.seatIndex - b.seatIndex;
    return ap < bp ? -1 : 1;
  });
  return applySortLayoutPins(sorted, sortLayoutPins);
}

export function buildPlacements(
  seats?: SeatData[],
  gridSize?: number,
  sortMode: BoardSortMode = 'floor',
  sortLayoutPins?: ReadonlyMap<number, number>,
  timeKeyOverride?: ReadonlyMap<number, number>,
): TilePlacement[] {
  const size = resolveGridSize(seats, gridSize);
  const total = size * size;
  const out: TilePlacement[] = new Array(total);
  const offset = (size - 1) * 0.5 * TILE_SPACING;

  const order: number[] = new Array(total);
  if (seats && seats.length > 0) {
    const sorted = sortedSeatsForBoard(seats, sortMode, sortLayoutPins, timeKeyOverride);
    for (let rank = 0; rank < total; rank++) {
      const seat = sorted[rank];
      order[rank] = seat ? seat.seatIndex : rank;
    }
  } else {
    for (let i = 0; i < total; i++) order[i] = i;
  }

  for (let rank = 0; rank < total; rank++) {
    const seatIndex = order[rank];
    // rank 0 → row 0 (visually top of the board) with col 0 (leftmost), so
    // the cheapest tile sits in the top-left corner. Cheaper tiles read as
    // "lower priced floor" up at the back of the board, more expensive
    // tiles march down towards the camera, ending bottom-right.
    const row = Math.floor(rank / size);
    const col = rank % size;
    // Seat index can exceed `total - 1` when the caller passes a partial
    // list (e.g. snapshot of 100 seats on a 16×16 board before padding).
    // Write by rank in that case so the array stays dense.
    const writeIndex = seatIndex < total ? seatIndex : rank;
    out[writeIndex] = {
      index: writeIndex,
      x: col * TILE_SPACING - offset,
      z: row * TILE_SPACING - offset,
      yJitter: 0,
      rotJitter: 0,
      scaleJitter: 1,
    };
  }
  return out;
}

function resolveGridSize(seats: SeatData[] | undefined, explicit?: number): number {
  if (explicit && explicit > 0) return explicit;
  if (seats && seats.length > 0) {
    return Math.max(1, Math.ceil(Math.sqrt(seats.length)));
  }
  return GRID_SIZE;
}

/**
 * World-space footprint of a `size × size` grid, i.e. the distance between
 * the centres of the first and last tile in a row. Handy for sizing the
 * plinth, computing camera framing, and anything else that needs to know
 * "how wide is this board in three.js units".
 */
export function boardFootprint(size: number): number {
  return Math.max(0, size - 1) * TILE_SPACING;
}

/**
 * Placement of the tile **one row above** the given one in the same column
 * (same `x`, next lower `z`). Returns `null` on the back row (no neighbour
 * toward −Z), e.g. top row of the board in the default camera layout.
 */
export function tilePlacementInColumnAbove(
  placements: readonly TilePlacement[],
  p: TilePlacement,
): TilePlacement | null {
  const inCol = [...placements]
    .filter((pl) => Math.abs(pl.x - p.x) < 1e-5)
    .sort((a, b) => a.z - b.z);
  const idx = inCol.findIndex((pl) => pl.index === p.index);
  if (idx <= 0) return null;
  return inCol[idx - 1]!;
}

export type TileTier = 'vacant' | 'owned' | 'elite' | 'taken-over' | 'reserved' | 'own';

/**
 * Map a seat to a visual tier for color + lift behaviour. `ownerAddress` lets
 * the lobby highlight a couple of tiles as "yours" for demo purposes.
 */
export function tileTier(seat: SeatData, demoOwnerIndex: number | null = null): TileTier {
  if (!seat.holder && !seat.isReserved) return 'vacant';
  if (seat.isForfeited || seat.isPendingForfeit) return 'taken-over';
  if (seat.isReserved) return 'reserved';
  // `seat.isOwn` is the source of truth; `demoOwnerIndex` remains a legacy
  // fallback for call sites that still pass a single index sentinel.
  if (seat.isOwn) return 'own';
  if (demoOwnerIndex !== null && seat.seatIndex === demoOwnerIndex) return 'own';
  const lvl = seat.holderLevel ?? 0;
  if ((seat.rank !== undefined && seat.rank <= 5) || lvl >= 4) return 'elite';
  return 'owned';
}

/**
 * Tier the seat would carry if the viewer didn't own it — i.e. `tileTier`
 * with the `isOwn` / `demoOwnerIndex` short-circuits removed. Used by the
 * 3D body-color pass so owned tiles inherit the same level/state surface
 * as their neighbours; the marker that flags them as yours is the
 * extruded cobalt frame drawn by `TileOwnedRimOverlay` (with the
 * tier edge ring in `TileInstanced` reserved for runway warnings).
 *
 * Keeping this distinct from `tileTier` (rather than threading a flag
 * through it) avoids any chance of a body-color caller accidentally
 * dropping the `own` tier elsewhere — every existing consumer that wants
 * the "owned" branch (overlay, plinth material, hover lift target)
 * continues to use `tileTier` unchanged.
 */
export function tileNaturalTier(seat: SeatData): TileTier {
  if (!seat.holder && !seat.isReserved) return 'vacant';
  if (seat.isForfeited || seat.isPendingForfeit) return 'taken-over';
  if (seat.isReserved) return 'reserved';
  const lvl = seat.holderLevel ?? 0;
  if ((seat.rank !== undefined && seat.rank <= 5) || lvl >= 4) return 'elite';
  return 'owned';
}

export const TIER_COLORS: Record<TileTier, { base: string; emissive: string; emissiveIntensity: number; metalness: number; roughness: number }> = {
  vacant:      { base: '#2a2d35', emissive: '#000000', emissiveIntensity: 0,    metalness: 0.15, roughness: 0.85 },
  owned:       { base: '#384158', emissive: '#0e1830', emissiveIntensity: 0.25, metalness: 0.55, roughness: 0.45 },
  elite:       { base: '#8a6a26', emissive: '#4a2f06', emissiveIntensity: 0.55, metalness: 0.85, roughness: 0.28 },
  'taken-over':{ base: '#5a381f', emissive: '#2a1808', emissiveIntensity: 0.3,  metalness: 0.7,  roughness: 0.5  },
  reserved:    { base: '#4a3870', emissive: '#241545', emissiveIntensity: 0.4,  metalness: 0.5,  roughness: 0.5  },
  own:         { base: '#2b4a7a', emissive: '#1d4ed8', emissiveIntensity: 0.85, metalness: 0.7,  roughness: 0.35 },
};
